import React from 'react';
import { Card, CardContent, Grid, Stack, SxProps, Theme } from '@mui/material';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import { useQuery } from '@tanstack/react-query';
import { GetAxiosInstance } from 'src/services/baseService';
import { VigoTooltip } from 'src/@vigo/vigo-tooltip';
import { useGetProductLevel } from 'src/@core/utils/getProductLevel';
import determineActiveColor from 'src/@core/utils/determineActiveColor';
import { useTheme } from '@mui/material';
import { LockOutline } from 'mdi-material-ui';

interface CountWidgetProps {
  serviceLocation?: string;
  endpoint?: string;
  filter?: string;
  subFilter?: string;
  title: string;
  width?: string;
  icon?: string;
  iconColor?: string;
  countColor?: string;
  titleColor?: string;
  subtitle?: string;
  selected?: boolean;
  onSelect?: (filter: string) => void;
  sx?: SxProps<Theme>;
  name: string;
  disableTooltip?: boolean;
  countOverride?: number;
  locked?: boolean;
}

const CountWidget = ({
  serviceLocation,
  endpoint,
  title,
  width: customWidth = '100%',
  //  countColor = 'initial',
  titleColor = 'initial',
  subtitle = '',
  filter,
  subFilter,
  onSelect,
  selected,
  name,
  sx = {},
  disableTooltip = false,
  countOverride,
  locked = false
}: CountWidgetProps) => {
  const theme = useTheme();

  const getColour = (_value: number): string => {
    const colour = theme.palette.mode === 'light' ? '#000' : '#fff';

    return colour;
  };

  const prod = useGetProductLevel();

  const { data: count } = useQuery({
    queryKey: [`countWidget-${title}`, title, serviceLocation, endpoint, filter],
    queryFn: async () => {
      let res: number;

      const params = { filter: filter, showDeleted: filter?.includes('deletedOn') ? true : false };

      if (serviceLocation !== '') {
        if (!endpoint?.includes('count')) {
          endpoint = endpoint + '/count';
        }
        const ret = await GetAxiosInstance().get<any[]>(`${serviceLocation}/${endpoint}`, { params });
        res = Array.isArray(ret.data) ? ret.data.length : Number(ret.data);
      } else {
        res = 0;
      }

      return res;
    },
    enabled: Boolean(serviceLocation) && Boolean(endpoint),
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000
  });

  const determineCountValue = (): string => {
    if (countOverride) {
      if (countOverride == 0) return '0';

      return Math.round(countOverride).toString();
    } else if (count) {
      if (count == 0) return '0';

      return Math.round(count).toString();
    } else {
      return '0';
    }
  };

  const clickMe = () => {
    if (onSelect && !locked) {
      onSelect(subFilter ? subFilter.toString() : '');
    }
  };

  return (
    <VigoTooltip title={title?.toString() ?? ''} disableHoverListener={disableTooltip}>
      <Card
        id={name}
        sx={{
          borderWidth: '4px',
          borderStyle: 'solid',
          borderColor: selected ? determineActiveColor(theme, prod) : 'transparent',
          padding: '0px',
          cursor: 'pointer',
          width: customWidth,
          height: '75px',
          ...sx
        }}
        variant="outlined"
        onClick={clickMe}>
        <CardContent
          sx={{
            cursor: 'pointer',
            padding: 1,
            pt: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            position: 'relative',
            height: '100%',
            width: '100%',
            pr: locked ? '30px' : 1
          }}>
          <Grid
            container
            direction="column"
            spacing={0}
            alignItems="flex-start"
            sx={{ cursor: 'pointer', padding: 0, margin: 0, flex: '1' }}>
            <Stack
              className="countWidget__contentContainer"
              sx={{ mt: '-2px', width: '100%', height: '100%', padding: 0 }}>
              <VigoTypography
                className="countWidget__title"
                // disableTranslation
                value={title}
                sx={{
                  textAlign: 'center',
                  cursor: 'pointer',
                  margin: '0px',
                  padding: '0px',
                  fontWeight: 'bold',
                  fontSize: '1.0rem'
                }}
              />
              <VigoTypography
                //@ts-ignore
                value={determineCountValue()}
                className="countWidget__count"
                sx={{
                  mt: '-14px',
                  textAlign: 'center',
                  padding: '0px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '2.5rem',
                  color: getColour(count ?? 0)
                }}
                disableTranslation
              />
              <VigoTypography
                disableTranslation
                value={subtitle}
                className="countWidget__subtitle"
                sx={{
                  mt: '-10px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  padding: '0px',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  color: { titleColor }
                }}
              />
              {locked && (
                <Grid
                  item
                  sx={{
                    position: 'absolute',
                    right: 8,
                    bottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 0,
                    pointerEvents: 'none'
                  }}>
                  <LockOutline sx={{ color: '#aaa' }} />
                </Grid>
              )}
            </Stack>
          </Grid>
        </CardContent>
      </Card>
    </VigoTooltip>
  );
};

export default CountWidget;
